import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, effect, inject, signal } from '@angular/core';
import { Observable, finalize, firstValueFrom, shareReplay } from 'rxjs';
import { environment as env } from '../../environments/environment';
import { CurrentRestaurantService, getCurrentRestaurantId } from './current-restaurant.service';
import { JwtService } from './jwt.service';

/** Mirrors backend `FiscalMode`. */
export enum FiscalMode {
  Real = 'real',
  Training = 'training',
}

/** Mirrors backend `FiscalScope`. */
export enum FiscalScope {
  Order = 'order',
  Payment = 'payment',
  Correction = 'correction',
  Closure = 'closure',
  PeriodClosure = 'period-closure',
  CashSession = 'cash-session',
  CashMovement = 'cash-movement',
  Event = 'event',
}

/** Mirrors backend `TechnicalEventSeverity`. */
export enum TechnicalEventSeverity {
  Info = 'info',
  Warning = 'warning',
  Critical = 'critical',
}

export interface TechnicalEvent {
  _id: string;
  type: string;
  severity: TechnicalEventSeverity;
  restaurantId: string | null;
  userId: string | null;
  payload: Record<string, unknown>;
  ip: string | null;
  occurredAt: string;
  mode: FiscalMode;
  fiscalNumber: number;
  signature: string;
  previousSignature: string | null;
  signedAt: string;
}

export interface JetPage {
  items: TechnicalEvent[];
  total: number;
  page: number;
  pageSize: number;
}

export interface JetFilters {
  types?: string[];
  severity?: TechnicalEventSeverity;
  mode?: FiscalMode;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}

export interface ChainAnomaly {
  type: string;
  fiscalNumber?: number;
  detail?: string;
}

export interface VerifyChainResult {
  scope: FiscalScope;
  mode: FiscalMode;
  restaurantId: string | null;
  checked: number;
  valid: boolean;
  anomalies: ChainAnomaly[];
  firstFiscalNumber: number | null;
  lastFiscalNumber: number | null;
}

@Injectable({ providedIn: 'root' })
export class FiscalService {
  private readonly http = inject(HttpClient);
  private readonly jwtService = inject(JwtService);
  private readonly currentRestaurant = inject(CurrentRestaurantService);

  /** null until resolved — the banner must not flash "real" before it knows. */
  readonly mode = signal<FiscalMode | null>(null);

  constructor() {
    // One restaurant can be in training while its neighbour is live; switching
    // without re-reading would leave the banner describing the previous one.
    effect(() => {
      this.currentRestaurant.currentId();
      void this.loadMode();
    });
  }

  get isTraining(): boolean {
    return this.mode() === FiscalMode.Training;
  }

  // app.component.ts calls loadMode() explicitly on boot AND on every
  // navigation where mode() is still null, while the effect() above ALSO
  // calls it reactively whenever currentId() changes — on a fresh page load
  // these overlap, firing two concurrent /fiscal/mode requests for the same
  // restaurant (confirmed in a QA network capture). Keyed by restaurantId
  // (not a single slot, unlike UsersService.getMe()) so that switching
  // restaurants mid-request can never resolve one restaurant's call with
  // another's in-flight response.
  private readonly modeRequests = new Map<string, Observable<{ mode: FiscalMode } | null>>();

  async loadMode(): Promise<void> {
    const restaurantId = getCurrentRestaurantId();
    if (!this.jwtService.isLogged() || !restaurantId) {
      this.mode.set(null);
      return;
    }
    try {
      let request$ = this.modeRequests.get(restaurantId);
      if (!request$) {
        request$ = this.http
          .get<{ mode: FiscalMode }>(`${env.apiUrl}/fiscal/mode`, { params: { restaurantId } })
          .pipe(
            finalize(() => this.modeRequests.delete(restaurantId)),
            shareReplay(1)
          );
        this.modeRequests.set(restaurantId, request$);
      }
      const response = await firstValueFrom(request$);
      this.mode.set(response?.mode ?? null);
    } catch {
      // A role without FISCAL.READ still has to use the POS: an unknown mode
      // hides the banner rather than blocking the app.
      this.mode.set(null);
    }
  }

  async setMode(mode: FiscalMode): Promise<FiscalMode> {
    const response = await firstValueFrom(
      this.http.post<{ mode: FiscalMode }>(`${env.apiUrl}/fiscal/mode`, {
        restaurantId: getCurrentRestaurantId(),
        mode,
      })
    );
    this.mode.set(response.mode);
    return response.mode;
  }

  resetTraining(): Promise<unknown> {
    return firstValueFrom(
      this.http.post(`${env.apiUrl}/fiscal/training/reset`, { restaurantId: getCurrentRestaurantId() })
    );
  }

  events(filters: JetFilters = {}, restaurantId?: string): Promise<JetPage> {
    let params = new HttpParams().set('restaurantId', restaurantId ?? getCurrentRestaurantId());
    if (filters.types?.length) params = params.set('types', filters.types.join(','));
    if (filters.severity) params = params.set('severity', filters.severity);
    if (filters.mode) params = params.set('mode', filters.mode);
    if (filters.from) params = params.set('from', filters.from);
    if (filters.to) params = params.set('to', filters.to);
    if (filters.page) params = params.set('page', String(filters.page));
    if (filters.pageSize) params = params.set('pageSize', String(filters.pageSize));

    return firstValueFrom(this.http.get<JetPage>(`${env.apiUrl}/fiscal/events`, { params }));
  }

  eventTypes(): Promise<{
    types: string[];
    severities: TechnicalEventSeverity[];
    criticalTypes: string[];
    warningTypes: string[];
  }> {
    return firstValueFrom(this.http.get<any>(`${env.apiUrl}/fiscal/events/types`));
  }

  verifyChain(scope: FiscalScope, mode: FiscalMode = FiscalMode.Real, restaurantId?: string): Promise<VerifyChainResult> {
    const params = new HttpParams()
      .set('restaurantId', restaurantId ?? getCurrentRestaurantId())
      .set('scope', scope)
      .set('mode', mode);
    return firstValueFrom(this.http.get<VerifyChainResult>(`${env.apiUrl}/fiscal/verify-chain`, { params }));
  }

  retention(): Promise<Record<string, unknown>> {
    return firstValueFrom(
      this.http.get<Record<string, unknown>>(`${env.apiUrl}/fiscal/retention`, {
        params: { restaurantId: getCurrentRestaurantId() },
      })
    );
  }

  archive(year: number, mode: FiscalMode = FiscalMode.Real): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(`${env.apiUrl}/fiscal/archives/${year}`, {
        params: { restaurantId: getCurrentRestaurantId(), mode },
      })
    );
  }

  version(): Promise<{ version: string }> {
    return firstValueFrom(this.http.get<{ version: string }>(`${env.apiUrl}/fiscal/version`));
  }
}
